/** @jsx jsx */
import React, { useCallback, useMemo, useEffect } from 'react';
import { css, styled, jsx } from '@compiled/react';
import meanBy from 'lodash/meanBy';
import Heading from '@atlaskit/heading';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { ProjectId } from '@atlassian/jira-shared-types';
import { HistoricalCycleTimeSkeleton } from '@atlassian/jira-skeletons';
import ErrorRetry from '@atlassian/jira-software-devops-report-common-error-retry';
import type { EChartsEvent } from '../../common/types';
import { PerformanceTracker } from '../../common/ui/performance-tracker';
import { pickDateRange } from '../../common/utils/date-range';
import { useSelectedRange } from '../../controllers/selected-date-range';
import { useTableFilterStore } from '../../controllers/top-filter';
import { useViewCycleTimeReportExperienceFail } from '../../services/experience-tracker';
import { useProjectId } from '../../services/get-project';
import { useHistoricalCycleTimeData } from '../../services/historical-cycle-time';
import {
	isDataEmpty,
	getSelectedBarChartBarDataAndItsIndex,
} from '../../services/historical-cycle-time/utils';
import Chart from './chart';
import { convertBarChartDataToChartDataPoints } from './chart/utils';
import messages from './messages';
import NoMatches from './no-matches';
import { SelectionDetail } from './selection-detail';
import { useSmartDateRangeSelect } from './utils';

export const HistoricalCycleTime = () => {
	const { formatMessage } = useIntl();
	const projectId: ProjectId | null = useProjectId();
	const [filters] = useTableFilterStore();
	const { data, loading, error, fetch } = useHistoricalCycleTimeData(projectId, filters);
	const [selectedDateRange, { setSelectedRange }] = useSelectedRange();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// @ts-expect-error - TS2345 - Argument of type '(range: DateRange) => void | Promise<void>' is not assignable to parameter of type '(range: DateRange) => Action<State>'.
	useSmartDateRangeSelect(data, selectedDateRange, setSelectedRange);
	const onViewCycleTimeReportExperienceFail = useViewCycleTimeReportExperienceFail();

	const onChartClicked = useCallback(
		({ data: eventData }: EChartsEvent) => {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'click',
					actionSubject: 'weeklyComparisonWeek',
				}),
			);
			setSelectedRange(pickDateRange(eventData.customInfo));
		},
		[createAnalyticsEvent, setSelectedRange],
	);
	const events = useMemo(() => ({ click: onChartClicked }), [onChartClicked]);

	useEffect(() => {
		if (error && data == null) {
			onViewCycleTimeReportExperienceFail('HistoricalCycleTimeChart', error);
			fireErrorAnalytics({
				meta: {
					id: 'HistoricalCycleTimeChartDataFailed',
					packageName: 'jiraSoftwareCycleTimeBreakdownReport',
					teamName: 'fusion-solaris',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	}, [data, error, onViewCycleTimeReportExperienceFail]);

	if (loading) {
		return <HistoricalCycleTimeSkeleton />;
	}

	if (error && data == null) {
		return (
			<div
				data-test-id="software-cycle-time-breakdown-report.ui.historical-cycle-time.error-ui-wrapper"
				css={errorUIWrapperStyles}
			>
				<ErrorRetry onRetry={fetch} />
			</div>
		);
	}
	if (data == null || selectedDateRange === null) {
		return <HistoricalCycleTimeSkeleton />;
	}
	if (error) {
		// TODO - Probably we would want to fire analytics?
	}

	const isDataEmptyState = isDataEmpty(data.barData);
	const dataPoints = convertBarChartDataToChartDataPoints(
		data,
		selectedDateRange,
		isDataEmptyState,
	);
	const { barData: selectedBarData } = getSelectedBarChartBarDataAndItsIndex(
		data.barData,
		selectedDateRange,
	);

	return (
		<div // Note: this test ID is used in a Pollinator test.
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			data-test-id="software-cycle-time-trend-report.ui.historical-cycle-time"
		>
			{/* Performance tracker is located here because this chart is the first "meaningful" data the report renders. */}
			<PerformanceTracker />

			<ChartTitle level="h400" as="h5">
				{formatMessage(messages.chartTitle)}
				<Tooltip
					content={formatMessage(messages.titleHelpTooltip)}
					position="right"
					onShow={() => {
						fireUIAnalytics(
							createAnalyticsEvent({
								action: 'hovered',
								actionSubject: 'weeklyComparisonHelp',
							}),
						);
					}}
				>
					<div css={questionIconStyles}>
						<QuestionCircleIcon size="small" label="" />
					</div>
				</Tooltip>
			</ChartTitle>

			<div
				data-test-id="software-cycle-time-breakdown-report.ui.historical-cycle-time.chart"
				css={chartStyles}
			>
				<div css={chartItemStyles}>
					<Chart
						dataPoints={dataPoints}
						medianInSeconds={
							data.hasEnoughDataForMeaningfulStatistics ? data.wholePeriodStatistics : null
						}
						displayUnits={
							// don't use median as it doesn't always exist
							meanBy(
								dataPoints.filter((dataPoint) => dataPoint.value),
								(dataPoint) => dataPoint.value,
							) >= 24
								? 'days'
								: 'hours'
						}
						onEvents={events}
					/>
				</div>
				{isDataEmptyState ? (
					<NoMatches />
				) : (
					<div css={sectionDetailItemStyles}>
						<SelectionDetail
							selectedBarData={selectedBarData}
							wholePeriodStatistics={
								data.hasEnoughDataForMeaningfulStatistics ? data.wholePeriodStatistics : null
							}
							hasPermissionForAllContributingIssues={data.hasPermissionForAllContributingIssues}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChartTitle = styled(Heading)({
	height: token('space.300', '24px'),
	display: 'flex',
	alignItems: 'center',
	margin: `0 ${token('space.100', '8px')} 0 0`,
	paddingLeft: token('space.250', '20px'),
});

const questionIconStyles = css({
	marginLeft: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	fill: token('color.icon.inverse', colors.N800),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon', colors.N80),
	cursor: 'pointer',
	paddingTop: token('space.025', '2px'),
});

const sectionDetailItemStyles = css({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	paddingBottom: token('space.800', '64px'),
});

const chartStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 31}px`,
	display: 'flex',
});

const chartItemStyles = css({
	width: 'calc(100% - 224px)',
});

const errorUIWrapperStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 53}px`,
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});
